import { BASE_URL, BRAND_CODE } from '../api/api';

export class WalletStreamManager {
  private abortController: AbortController | null = null;
  private isConnecting = false;
  private active = false;

  public connect(token: string) {
    if (this.active || this.isConnecting) return;
    this.active = true;
    this.isConnecting = true;
    this.startFetch(token);
  }

  public disconnect() {
    this.active = false;
    this.isConnecting = false;
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  private async startFetch(token: string) {
    this.abortController = new AbortController();

    try {
      const response = await fetch(`${BASE_URL}/v2/brands/${BRAND_CODE}/wallet/stream`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream'
        },
        signal: this.abortController.signal
      });

      if (!response.ok) {
        console.error('[WalletStreamManager] Failed to connect', response.status);
        this.reconnect(token);
        return;
      }

      this.isConnecting = false;
      const reader = response.body?.getReader();
      if (!reader) return;

      const decoder = new TextDecoder();
      let buffer = '';

      while (this.active) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        
        // keep the last chunk if it doesn't end with a newline
        buffer = lines.pop() || '';

        let currentEvent = '';
        let currentData = '';

        for (const line of lines) {
          if (line.trim() === '') {
            // End of message
            if (currentEvent === 'balance' && currentData) {
              try {
                const payload = JSON.parse(currentData);
                window.dispatchEvent(new CustomEvent('wallet-stream-updated', { detail: payload }));
              } catch (e) {
                console.error('[WalletStreamManager] Parse error', e);
              }
            }
            currentEvent = '';
            currentData = '';
          } else if (line.startsWith('event:')) {
            currentEvent = line.substring(6).trim();
          } else if (line.startsWith('data:')) {
            currentData += line.substring(5).trim();
          }
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('[WalletStreamManager] Fetch error', err);
      }
    } finally {
      if (this.active) {
        this.reconnect(token);
      }
    }
  }

  private reconnect(token: string) {
    this.isConnecting = false;
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    if (this.active) {
      setTimeout(() => this.connect(token), 3000);
    }
  }
}

export const walletStreamManager = new WalletStreamManager();
